// CodeController — appels API du projet d'un candidat (CCT-73).
// Le serveur scope tout à l'email du token : un candidat ne peut lire/écrire que SON projet.
import { apiFetch } from './client';

export type FileMode = 'read_only' | 'read_write';

export interface ProjectFile {
  path: string;
  content: string;
  /** `read_only` = fourni par l'auteur (ou log d'exécution) : l'API en REFUSE l'écriture. */
  mode: FileMode;
}

export interface Project {
  projectid: number;
  templateid: number | null;
  missionid: number | null;
  elementid: string | null;
  language: string;
  /** Fichier lancé par « Run ». */
  entrypoint: string | null;
  files: ProjectFile[];
  /** Capacités décidées par l'AUTEUR sur la template (CCT-77). Le serveur les applique
   *  aussi : masquer un bouton ne protège rien, l'appel HTTP reste forgeable. */
  allowFolders: boolean;
  allowUpload: boolean;
}

export interface RunResult { runId: number; logPath: string; status: string }

const A = (token: string | null) => ({ token, auth: true as const });

/** Idempotent : rouvrir l'IDE rend le projet EXISTANT, jamais une copie neuve de la template
 *  (sinon le candidat perdrait son code en revenant sur l'élément). */
export const instantiateProject = (token: string | null, templateId: number, missionId: number, elementId: string) =>
  apiFetch<Project>('/projects/instantiate', {
    ...A(token), method: 'POST', body: { templateId, missionId, elementId },
  });

export const getProject = (token: string | null, projectId: number) =>
  apiFetch<Project>(`/projects/${projectId}`, A(token));

export const saveProjectFile = (token: string | null, projectId: number, path: string, content: string) =>
  apiFetch<{ saved: boolean }>(`/projects/${projectId}/files`, {
    ...A(token), method: 'PUT', body: { path, content },
  });

/** Crée un fichier. `uploaded` = import depuis l'app (exige allowUpload côté serveur). */
export const addProjectFile = (token: string | null, projectId: number, path: string,
  content: string, uploaded = false) =>
  apiFetch<{ path: string; mode: FileMode }>(`/projects/${projectId}/files`, {
    ...A(token), method: 'POST', body: { path, content, uploaded },
  });

/** Déplace un fichier vers un autre dossier (`toDir` vide = racine). */
export const moveProjectFile = (token: string | null, projectId: number, path: string, toDir: string) =>
  apiFetch<{ path: string }>(`/projects/${projectId}/files/move`, {
    ...A(token), method: 'PATCH', body: { path, toDir },
  });

export const renameProjectFile = (token: string | null, projectId: number, path: string, newName: string) =>
  apiFetch<{ path: string }>(`/projects/${projectId}/files/rename`, {
    ...A(token), method: 'PATCH', body: { path, newName },
  });

export const deleteProjectFile = (token: string | null, projectId: number, path: string) =>
  apiFetch<{ deleted: string }>(`/projects/${projectId}/files?path=${encodeURIComponent(path)}`, {
    ...A(token), method: 'DELETE',
  });

export const renameProjectDir = (token: string | null, projectId: number, path: string, newName: string) =>
  apiFetch<{ path: string }>(`/projects/${projectId}/dirs/rename`, {
    ...A(token), method: 'PATCH', body: { path, newName },
  });

export const deleteProjectDir = (token: string | null, projectId: number, path: string) =>
  apiFetch<{ deleted: string; count: number }>(`/projects/${projectId}/dirs?path=${encodeURIComponent(path)}`, {
    ...A(token), method: 'DELETE',
  });

/** Archive une exécution ET écrit `logs/run-N.log` dans le projet (côté serveur). */
export const recordRun = (token: string | null, projectId: number,
  r: { status: string; stdout: string; stderr: string; durationMs: number }) =>
  apiFetch<RunResult>(`/projects/${projectId}/runs`, { ...A(token), method: 'POST', body: r });
